import api from "../../../core/api";

// =====================================================================
//  machineLiveStatusApi (DR-3) — thin customer-facing wrapper over the
//  already-live MachineGateway single-machine status endpoint. Used by the
//  machine-profile readiness card to decide CONNECTED vs not.
//
//  Deliberately NOT importing `internal/edgeMonitor/edgeMonitorApi.ts` — that
//  client is internal-tool-only (MIDAS_DATA_READINESS_UI_SPEC §5.4). This is a
//  standalone, minimal, fail-closed wrapper.
//
//  SEC-REQ-9 (fail-closed): ANY error (403 cross-tenant, network, 5xx, empty)
//  collapses to `{ connected: false }`. A machine is only ever reported
//  CONNECTED on a positive 200 with a live (non-Offline) status row.
//
//  Endpoint: GET /v1/gateway/machines/{id}/status  (Machinery:Read,
//  customer-scoped; 404 MACHINE_NOT_FOUND on a foreign machine).
//  Base URL = window.app.config.machineGatewayApiBaseUrl (the gateway mount).
// =====================================================================

// ---------------------------------------------------------------------
//  ONE shared "is this a genuinely-live status" rule (SEC-REQ-9, fail-closed).
//
//  Mirrors `MachineStatusCode` (internal/edgeMonitor/models/edgeMonitorModels
//  .ts) / server `MachineStatus.cs`:
//    Running(1) Idle(2) Stopped(3) Alarm(4) → agent connected AND telemetry
//      feed live → CONNECTED.
//    Offline(5)         → heartbeat dead                → NOT connected.
//    TelemetryStale(6)  → agent heartbeating, feed dead → NOT connected.
//
//  Two wire shapes reach this rule, so it accepts both and fails closed on
//  anything else (missing / null / unexpected):
//   - single-machine `GET .../{id}/status` serializes the enum as a NUMBER.
//   - list `GET /v1/gateway/machines` (GatewayMachineSummaryDto) serializes the
//     decoded status as a STRING ("Running" | "Idle" | …).
// ---------------------------------------------------------------------
const LIVE_STATUS_CODES: ReadonlyArray<number> = [1, 2, 3, 4];
const LIVE_STATUS_NAMES: ReadonlyArray<string> = ["Running", "Idle", "Stopped", "Alarm"];

/**
 * Genuinely-live? True ONLY for a whitelisted live code/name. Offline(5),
 * TelemetryStale(6), and any unexpected shape (missing / non-numeric,
 * non-string / null) → false (fail-closed).
 */
export function isLiveStatus(status: unknown): boolean {
  if (typeof status === "number") return LIVE_STATUS_CODES.includes(status);
  if (typeof status === "string") return LIVE_STATUS_NAMES.includes(status);
  return false;
}

interface GatewayStatusResponse {
  machineId: string;
  status: number; // MachineStatusCode enum (numeric on this endpoint)
  updatedAt?: string;
}

/** Minimal shape of a `GatewayMachineSummaryDto` row (list endpoint). */
interface GatewayMachineSummaryResponse {
  machineId: string;
  status?: string | null; // decoded enum name, or null when no status row yet
}

export interface MachineLiveStatus {
  connected: boolean;
}

const getRequestConfig = () => {
  const baseURL = (window as any).app?.config?.machineGatewayApiBaseUrl;
  return baseURL ? { baseURL } : {};
};

class MachineLiveStatusApi {
  /**
   * Returns `{ connected }` for a machine. Fail-closed: connected is true ONLY
   * when the gateway returns a status row whose status is not Offline. Any
   * error / null body → `{ connected: false }`.
   */
  getConnected(machineId: string): Promise<MachineLiveStatus> {
    return api
      .get<GatewayStatusResponse | null>(
        `/v1/gateway/machines/${machineId}/status`,
        getRequestConfig()
      )
      .then((x) => ({ connected: isLiveStatus(x.data?.status) }))
      .catch(() => ({ connected: false }));
  }

  /**
   * BATCH connected-state read for the Machinery overview (DR-4). ONE call for
   * the whole page — never per-card. Hits the no-parameter list endpoint
   * `GET /v1/gateway/machines` (`ListAsync`), which returns the caller's full
   * customer-scoped set of gateway machines WITH their decoded status — same
   * tenant guard as the per-id endpoint, but with NO query string, so it can
   * never trip the IIS URL-length ceiling (404.15) that the old
   * `?ids=…&ids=…` batch hit at ~49 machines.
   *
   * Returns the SET of machine ids whose status is genuinely live (via
   * `isLiveStatus`). Fail-closed (SEC-REQ-9): any error → empty set; a machine
   * absent from the response, or with a non-live/absent status, is simply not
   * in the set.
   */
  getConnectedIds(): Promise<Set<string>> {
    return api
      .get<GatewayMachineSummaryResponse[] | null>(
        `/v1/gateway/machines`,
        getRequestConfig()
      )
      .then((x) => {
        const rows = x.data ?? [];
        const set = new Set<string>();
        for (const row of rows) {
          if (row && row.machineId && isLiveStatus(row.status)) {
            set.add(row.machineId);
          }
        }
        return set;
      })
      .catch(() => new Set<string>());
  }
}

export default new MachineLiveStatusApi();
